import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ClientRequestApiService } from '@aiwyn/api';
import { CommentsApiService } from '../../client-requests/components/comments/comments-api.service';
import { Comment, MessageEntityType } from '../../client-requests/components/comments/comments.model';

/**
 * CXP-owned facade over the app-local threaded-messaging API
 * (`CommentsApiService` — Apollo `message` query + `CreateMessage` mutation,
 * ART-6424). It exists so the CRL overlay's Comments tab can read and post
 * threaded comments WITHOUT importing the legacy `ClientRequestsModule`: the
 * legacy `CommentsState`/`CommentsService`/`CommentsSelectors` are coupled to
 * `ClientRequestsState`/`CommonClientRequestsState` (restriction masking +
 * activity merge) and cannot be registered standalone, but the underlying
 * `CommentsApiService` is `providedIn: 'root'` and depends only on Apollo +
 * `AuthService`. This is the same "reuse the app-local data layer, not the
 * legacy module" approach used for contacts.
 *
 * Scope is always a single CRL request ITEM, so the entity type is fixed to
 * `CLIENT_REQUEST_ITEM` and the entity id is the item id. Keeping this in
 * `cxp/services/` honours domain-layer isolation (only services here consume
 * the API layer); the component talks to this facade, never to Apollo.
 */
@Injectable({ providedIn: 'root' })
export class CxpCrlCommentsService {
  private readonly commentsApi = inject(CommentsApiService);
  private readonly clientRequestApi = inject(ClientRequestApiService);

  /** Top-level comments (with their replies) for a single request item, newest first. */
  loadComments(itemId: number): Observable<Comment[]> {
    return this.commentsApi.getMessages([itemId], MessageEntityType.CLIENT_REQUEST_ITEM);
  }

  /**
   * Post a top-level comment (no `parentId`) or a reply (with the parent
   * comment id) against a request item. Resolves to the created comment.
   */
  postComment(itemId: number, text: string, parentId?: string | null): Observable<Comment> {
    return this.commentsApi.createMessage({
      entityType: MessageEntityType.CLIENT_REQUEST_ITEM,
      entityId: itemId,
      content: text,
      parentMessageId: parentId ? Number(parentId) : null,
    });
  }

  /**
   * Records the caller as having viewed the given messages — one batched write
   * covering every (message, linked contact id) pair. Ids arrive as the `Comment`
   * string ids the thread renders; unparseable ones are dropped rather than sent
   * as `NaN`. A write with nothing to say resolves without touching the network.
   *
   * Goes to `ClientRequestApiService` rather than the legacy
   * `CommentsApiService.markMessagesAsViewed`, whose document declares
   * `$objects: [message_viewed_insert_input!]!` — a variable TYPE the tenant
   * rewrite does not rename, so the server rejects it and every write is lost.
   */
  markCommentsViewed(commentIds: string[], contactIds: number[]): Observable<number> {
    const messageIds = commentIds.map(Number).filter((id) => Number.isFinite(id));
    if (messageIds.length === 0 || contactIds.length === 0) {
      return of(0);
    }
    return this.clientRequestApi.markMessagesViewed(messageIds, contactIds);
  }
}
